(function () {
    'use strict';

    angular
        .module('app')
        .controller('EmailTemplateEditCtrl', EmailTemplateEditCtrl);

    EmailTemplateEditCtrl.$inject = [
        '$location',
        '$scope',
        '$q',
        '$routeParams',
        'EmailTemplatesService',
        'EntityTemplatesService',
        'LastUpdatedService',
        'NavigationAidService',
        'DialogService',
        'UnsavedChangesService',
        'EmailTemplate',
        'ChilliUserProfileService',
        'ObjectSaveErrorHandlerService',
        'WindowTitleService',
        'DocumentTemplatesService'];

    /**
     * @ngdoc controller
     * @name EmailTemplateEditCtrl
     *
     * @description
     * Edit screen for an email template. Add a new one, or edit an existing one.
     */
    function EmailTemplateEditCtrl(
        $location,
        $scope,
        $q,
        $routeParams,
        EmailTemplatesService,
        EntityTemplatesService,
        LastUpdatedService,
        NavigationAidService, 
        DialogService, 
        UnsavedChangesService,
        EmailTemplate,
        ChilliUserProfileService,
        ObjectSaveErrorHandlerService,
        WindowTitleService,
        DocumentTemplatesService) {

        /* jshint validthis:true */
        var vm = this;
        vm.template = {};
        vm.entityTemplates = [];
        vm.saveTemplate = saveTemplate;
        vm.copyTemplate = copyTemplate;
        vm.deleteTemplate = deleteTemplate;
        vm.loading = true;
        vm.notFound = false;
        vm.permissionDenied = false;
        vm.viewEntities = viewEntities;
        Object.defineProperty(vm, 'dirty', {
            get: function () { return !_.isNil(UnsavedChangesService) && UnsavedChangesService.dirty; }
        });
        vm.close = close;
        Object.defineProperty(vm, 'deleteEnabled', {
            get: function () { return !_.isNil(vm.template) && !_.isNil(vm.template.data) && !_.isNil(vm.template.data.Id) && vm.template.data.Id != 'new'; }
        });
        Object.defineProperty(vm, 'selectedEntityTemplate', {
            get: getSelectedEntityTemplate,
            set: setSelectedEntityTemplate
        });
        Object.defineProperty(vm, 'lastUpdatedLabel', { get: function () {
            return LastUpdatedService.getLabel(vm.template.data);
        }
        });
        Object.defineProperty(vm, 'templateIsNew', {
            get: function () { return !($routeParams.id && $routeParams.id !== 'new'); }
        });
        Object.defineProperty(vm, 'selectedPreheaderDocumentTemplate', {
            get: getSelectedPreheaderDocumentTemplate,
            set: setSelectedPreheaderDocumentTemplate
        });
        Object.defineProperty(vm, 'selectedSubjectDocumentTemplate', {
            get: getSelectedSubjectDocumentTemplate,
            set: setSelectedSubjectDocumentTemplate
        });
        Object.defineProperty(vm, 'selectedBodyDocumentTemplate', {
            get: getSelectedBodyDocumentTemplate,
            set: setSelectedBodyDocumentTemplate
        });
        vm.viewTitle = 'Email Template';
        vm.currentTabPage = 'details';
        vm.preheaderDocumentTemplates = [];
        vm.subjectDocumentTemplates = [];
        vm.bodyDocumentTemplates = [];
        vm.toEmailContacts = [];
        vm.ccEmailContacts = [];
        vm.bccEmailContacts = [];
        vm.replyToEmailContacts = [];

        activate();

        function activate() {
            updateViewTitle();

            ChilliUserProfileService.verifyUserIsAdministrator({
                onUserIsAdmin: loadEverything,
                onUserIsNotAdmin: function () { vm.permissionDenied = true; },
                onError: function (response) { DialogService.error('Error loading User profile', response); },
                onFinally: function () {
                    vm.loading = false;
                    updateViewTitle();
                }
            });
        }

        function loadEverything() {
            var promiseList = [];
            promiseList.push(loadTemplate());
            promiseList.push(loadEntityTemplates());

            return $q.all(promiseList).then(function () {
                // Finish view-model configuration once everything critical is loaded
                UnsavedChangesService.register({
                    scope: $scope,
                    getDirtyFunction: getModelDirty,
                    saveFunction: saveTemplate,
                    unsavedItemName: 'Email Template'
                });

                // Copy the existing contacts, otherwise they won't show in the contact selector
                vm.ccEmailContacts = angular.copy(vm.template.data.Cc);
                vm.bccEmailContacts = angular.copy(vm.template.data.Bcc);
                vm.replyToEmailContacts = angular.copy(vm.template.data.ReplyTo);

                setDefaultReplyTo();

                return loadEntityTemplateItems(vm.template.data.EntityTemplateId);
            });
        }

        /**
         * Load email template into vm.template.
         *
         * @returns {Promise} A promise resolved once loading is finished. Rejects if load fails.
         */        
        function loadTemplate() {
            var getTemplatePromise;
            if (!vm.templateIsNew) {
                getTemplatePromise = EmailTemplate.get($routeParams.id);
            } else if ($routeParams.copyEmailTemplateId) {
                getTemplatePromise = EmailTemplate.getCopy($routeParams.copyEmailTemplateId);
            } else {
                getTemplatePromise = EmailTemplate.getNew(parseInt($routeParams.entityTemplateId));
            }

            return getTemplatePromise.then(
                function ok(template) {
                    vm.template = template;
                },
                function error(response) {
                    if (response.status === 404) {
                        vm.notFound = true;
                    } else {
                        DialogService.error('Error fetching Email Template', response);
                    }
                    return $q.reject(response);
                });
        }

        /**
         * Load entity templates and entity template count into vm.entityTemplates and vm.entityTemplatesCount.
         *
         * @returns {Promise} A promise resolved once loading is finished, resolves regardless of error.
         */
        function loadEntityTemplates() {
            return EntityTemplatesService.query()
                .then(getEntityTemplatesOk, getEntityTemplatesError);

            function getEntityTemplatesOk(data) {
                vm.entityTemplates = data.EntityTemplates;
            }

            function getEntityTemplatesError(response) {
                DialogService.error('Error fetching Entity Templates', response);
            }
        }

        /**
         *
         * Loads all items (document templates, contacts etc.) for the specified entity template
         *
         * @returns {Promise} A promise resolved once loading is finished.
         */
        function loadEntityTemplateItems(entityTemplateId) {
            if (!_.isNil(entityTemplateId)) {
                return $q.all([loadDocumentTemplates(entityTemplateId), loadContacts(entityTemplateId)]);
            } else {
                vm.preheaderDocumentTemplates = [];
                vm.subjectDocumentTemplates = [];
                vm.bodyDocumentTemplates = [];
                vm.toEmailContacts = [];

                return $q.when(true);
            }
        }

        /**
         *
         * Load document templates
         *
         * @returns {Promise} A promise resolved once loading is finished, resolves regardless of error.
         */
        function loadDocumentTemplates(entityTemplateId) {
            var promiseList = [];
            promiseList.push(EntityTemplatesService.getDocumentTemplates(entityTemplateId, 'EmailPreheader').then(getPreheaderDocumentTemplatesOk).catch(getDocumentTemplatesError));
            promiseList.push(EntityTemplatesService.getDocumentTemplates(entityTemplateId, 'EmailSubject').then(getSubjectDocumentTemplatesOk).catch(getDocumentTemplatesError));
            promiseList.push(EntityTemplatesService.getDocumentTemplates(entityTemplateId, 'EmailBody').then(getBodyDocumentTemplatesOk).catch(getDocumentTemplatesError));

            return $q.all(promiseList);

            function getPreheaderDocumentTemplatesOk(data) {
                vm.preheaderDocumentTemplates = data;

                if (!_.isNil(vm.template) && !_.isNil(vm.template.data) && !_.isNil(vm.template.data.Preheader) && vm.template.data.Preheader !== '') {
                    return loadSelectedDocumentTemplate(vm.template.data.Preheader, vm.preheaderDocumentTemplates);
                }
            }

            function getSubjectDocumentTemplatesOk(data) {
                vm.subjectDocumentTemplates = data;

                if (!_.isNil(vm.template) && !_.isNil(vm.template.data) && !_.isNil(vm.template.data.Subject) && vm.template.data.Subject !== '') {
                    return loadSelectedDocumentTemplate(vm.template.data.Subject, vm.subjectDocumentTemplates);
                }
            }

            function getBodyDocumentTemplatesOk(data) {
                vm.bodyDocumentTemplates = data;

                if (!_.isNil(vm.template) && !_.isNil(vm.template.data) && !_.isNil(vm.template.data.Body) && vm.template.data.Body !== '') {
                    return loadSelectedDocumentTemplate(vm.template.data.Body, vm.bodyDocumentTemplates);
                }
            }

            function getDocumentTemplatesError(response) {
                vm.error = 'The document templates were unsuccessfully retrieved.';
            }
        }

        function loadSelectedDocumentTemplate(id, array) {
            if (!_.some(array, function (item) { return item.Id === id; })) {
                return DocumentTemplatesService.getDocumentTemplate(id, true)
                    .then(function (result) { return [result, array]; })
                    .then(loadSelectedDocumentTemplateOk, loadSelectedDocumentTemplateError);
            }
        }

        function loadSelectedDocumentTemplateOk(result) {
            if (!_.isNil(result) && result.length === 2 && !_.isNil(result[0])) {
                result[1].push(result[0]);
            }
        }

        function loadSelectedDocumentTemplateError(response) {
            vm.error = 'The document templates were unsuccessfully retrieved.';
        }

        /**
         *
         * Load contacts
         *
         * @returns {Promise} A promise resolved once loading is finished, resolves regardless of error.
         */
        function loadContacts(entityTemplateId) {
            return EntityTemplatesService.getContacts(entityTemplateId, ['Email'])
                    .then(getContactsOk, getContactsError);

            function getContactsOk(data) {
                vm.toEmailContacts = angular.copy(data);
            }

            function getContactsError(response) {
                vm.error = 'The email contacts were not successfully retrieved.';
            }
        }

        /**
         * Update Title to be shown at the top of the view and browser window/tab title.
         */
        function updateViewTitle() {
            var title = 'Email Template';

            if (!_.isNil($routeParams.id) && $routeParams.id === 'new') {
                title = 'Add ' + title;
            }

            if (!_.isNil(vm.template) && !_.isNil(vm.template.data) && !_.isNil(vm.template.data.Name) && vm.template.data.Name !== '') {
                title += ' - ' + vm.template.data.Name;
            }

            vm.viewTitle = title;
            WindowTitleService.setTitle(title);
        }

        /**
         * @returns {bool} True if the model is dirty aka there are unsaved changes.
         */
        function getModelDirty() {
            return vm.template !== null && vm.template.dirty;
        }

        /**
         * Save the email template
         *
         * @returns {Promise} A promise that resolves once saving is finished.
         */
        function saveTemplate() {
            var saveRequest;

            if (!_.isNil(vm.addTemplateForm) && vm.addTemplateForm.$invalid) {
                return DialogService.messageList('Error saving email template', null, vm.addTemplateForm.$error)
                    .then(function () {
                        return $q.reject();
                    });
            } else {

                saveRequest = angular.copy(vm.template.data);

                return EmailTemplatesService.save(saveRequest)
                    .then(saveTemplateOk, saveTemplateError);
            }

            function saveTemplateOk(response) {
                if (vm.templateIsNew) {
                    UnsavedChangesService.clearRegistration();
                    NavigationAidService.replacePage('emailtemplates/' + response);
                } else {
                    return EmailTemplate.createWithData(response)
                        .then(function (template) {
                            vm.template = template;
                        });
                }
            }

            function saveTemplateError(response) {
                return ObjectSaveErrorHandlerService.handleObjectSaveError(
                    response,
                    vm.template.data,
                    'Email Template',
                    vm.saveTemplate,
                    function () {
                        return EmailTemplate.get($routeParams.id)
                            .then(function (getTemplateResponse) {
                                return getTemplateResponse.data;
                            });
                    });
            }
        }

        /**
         * Deletes the email template. The user is presented with a confirmation dialog.
         *
         * NB: This function is asynchronous.
         */
        function deleteTemplate() {
            if ($routeParams.id && $routeParams.id !== 'new') {
                vm.template.delete()
                    .then(deleteTemplateOk, deleteTemplateError);
            } else {
                promptUserToSaveForOperation('deleting');
            }

            function deleteTemplateOk(success) {
                NavigationAidService.goBack();
            }

            function deleteTemplateError(response) {
                if (!_.isNil(response)) {
                    DialogService.error('Error deleting Email Template', response);
                }
            }
        }

        function getSelectedEntityTemplate() {
            if (_.isNil(vm.template) || _.isNil(vm.template.data) || _.isNil(vm.template.data.EntityTemplateId)) {
                return null;
            } else {
                var searchResult = _.find(vm.entityTemplates, function (i) { return i.Id === vm.template.data.EntityTemplateId; });
                return angular.isUndefined(searchResult) ? null : searchResult;
            }
        }

        function setSelectedEntityTemplate(newTemplate) {
            if (newTemplate === null) {
                vm.template.data.EntityTemplateId = null;
            } else {
                vm.template.data.EntityTemplateId = newTemplate.Id;
            }

            setDefaultReplyTo();
            // Clear out these values since they are entity template specific
            vm.template.data.Preheader = null;
            vm.template.data.Subject = null;
            vm.template.data.Body = null;
            vm.template.data.To = [];

            return loadEntityTemplateItems(vm.template.data.EntityTemplateId);
        }

        function getSelectedPreheaderDocumentTemplate() {
            var value = null;

            if (!_.isNil(vm.template) && !_.isNil(vm.template.data) && !_.isNil(vm.template.data.Preheader) && vm.template.data.Preheader !== '') {
                value = _.find(vm.preheaderDocumentTemplates, function (documentTemplate) {
                    return documentTemplate.Id === vm.template.data.Preheader;
                });
            }

            if (getSelectedPreheaderDocumentTemplate.value !== value) {
                getSelectedPreheaderDocumentTemplate.value = value;
            }

            return getSelectedPreheaderDocumentTemplate.value;
        }

        function setSelectedPreheaderDocumentTemplate(newTemplate) {
            var value = null;
            if (!_.isNil(newTemplate) && newTemplate !== '') {
                value = newTemplate.Id;
            }

            if (!_.isNil(vm.template) && !_.isNil(vm.template.data)) {
                vm.template.data.Preheader = value;
            }
        }

        function getSelectedSubjectDocumentTemplate() {
            var value = null;

            if (!_.isNil(vm.template) && !_.isNil(vm.template.data) && !_.isNil(vm.template.data.Subject) && vm.template.data.Subject !== '') {
                value = _.find(vm.subjectDocumentTemplates, function (documentTemplate) {
                    return documentTemplate.Id === vm.template.data.Subject;
                });
            }

            if (getSelectedSubjectDocumentTemplate.value !== value) {
                getSelectedSubjectDocumentTemplate.value = value;
            }

            return getSelectedSubjectDocumentTemplate.value;
        }

        function setSelectedSubjectDocumentTemplate(newTemplate) {
            var value = null;
            if (!_.isNil(newTemplate) && newTemplate !== '') {
                value = newTemplate.Id;
            }

            if (!_.isNil(vm.template) && !_.isNil(vm.template.data)) {
                vm.template.data.Subject = value;
            }
        }

        function getSelectedBodyDocumentTemplate() {
            var value = null;

            if (!_.isNil(vm.template) && !_.isNil(vm.template.data) && !_.isNil(vm.template.data.Body) && vm.template.data.Body !== '') {
                value = _.find(vm.bodyDocumentTemplates, function (documentTemplate) {
                    return documentTemplate.Id === vm.template.data.Body;
                });
            }

            if (getSelectedBodyDocumentTemplate.value !== value) {
                getSelectedBodyDocumentTemplate.value = value;
            }

            return getSelectedBodyDocumentTemplate.value;
        }

        function setSelectedBodyDocumentTemplate(newTemplate) {
            var value = null;
            if (!_.isNil(newTemplate) && newTemplate !== '') {
                value = newTemplate.Id;
            }

            if (!_.isNil(vm.template) && !_.isNil(vm.template.data)) {
                vm.template.data.Body = value;
            }
        }

        /**
         * Sets the default reply to email contacts
         */
        function setDefaultReplyTo() {
            var selectedEntityTemplate = getSelectedEntityTemplate();
            if (!_.isNil(selectedEntityTemplate)) {
                vm.replyToEmailContacts = [{ Name: selectedEntityTemplate.PluralName + ' (Current ' + selectedEntityTemplate.Name + ')', FieldReference: '$.Email Address' }];
            } else {
                vm.replyToEmailContacts = [];
            }     
        }

        /**
         * Redirects the user to a view with a copy of the current email template.
         */
        function copyTemplate() {
            var copyUrl = 'emailtemplates/new?copyEmailTemplateId=';

            if (vm.templateIsNew) {
                promptUserToSaveForOperation('copying');
            } else {
                if (UnsavedChangesService.dirty) {
                    DialogService.unsavedChanges('Email Template')
                        .then(function () {
                            saveTemplate().then(function () {
                                $location.url(copyUrl + vm.template.data.Id);
                            });
                        });
                } else {
                    $location.url(copyUrl + vm.template.data.Id);
                }
            }
        }

        /*
         * When conducting an operation which requires a saved email template and the email template is new, a 'save email template' prompt needs to be shown
         *
         * @param {string} operationName - Name of operation that was being conducted
         */
        function promptUserToSaveForOperation(operationName) {
            return DialogService.show({
                controller: 'SaveItemDialogCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/controls/dialog/save-item-dialog.tmpl.html',
                locals: { name: 'email template', operation: operationName }
            })
            .then(function () {
                return saveTemplate();
            });
        }

        function close() {
            NavigationAidService.goBack();
        }

        /**
         * Redirects the user to a list view of entities for the entity template.
        */
        function viewEntities() {
            if (vm.templateIsNew) {
                promptUserToSaveForOperation('viewing entities');
            } else {
                if (UnsavedChangesService.dirty) {
                    DialogService.unsavedChanges('Email Template')
                        .then(function () {
                            saveTemplate().then(function () {
                                $location.url('entities?entityTemplateId=' + vm.template.data.EntityTemplateId);
                            });
                        });
                } else {
                    $location.url('entities?entityTemplateId=' + vm.template.data.EntityTemplateId);
                }
            }
        }
    }
})();